import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { CheckCircle, Warning, CircleNotch, EnvelopeSimple } from '@phosphor-icons/react';
import { API } from '../../utils/api';
import axios from 'axios';

export default function VerifyEmailPage() {
  const { language } = useLanguage();
  const { isAuthenticated, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('verifying'); // verifying, success, error, no-token
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const t = {
    en: {
      verifying: "Verifying your email...",
      successTitle: "Email verified!",
      successMessage: "Your email has been verified successfully. You can now access all features.",
      goToDashboard: "Go to Dashboard",
      login: "Log in",
      errorTitle: "Verification failed",
      invalidToken: "This verification link is invalid or has expired.",
      resendTitle: "Verify your email",
      resendMessage: "We sent you a verification email. Please check your inbox and click the link to verify your account.",
      resend: "Resend verification email",
      resending: "Sending...",
      resent: "Email sent! Check your inbox."
    },
    fr: {
      verifying: "Vérification de votre email...",
      successTitle: "Email vérifié!",
      successMessage: "Votre email a été vérifié avec succès. Vous pouvez maintenant accéder à toutes les fonctionnalités.",
      goToDashboard: "Aller au tableau de bord",
      login: "Se connecter",
      errorTitle: "Échec de la vérification",
      invalidToken: "Ce lien de vérification est invalide ou a expiré.",
      resendTitle: "Vérifiez votre email",
      resendMessage: "Nous vous avons envoyé un email de vérification. Veuillez vérifier votre boîte de réception et cliquer sur le lien.",
      resend: "Renvoyer l'email de vérification",
      resending: "Envoi...",
      resent: "Email envoyé! Vérifiez votre boîte de réception."
    }
  };

  const text = t[language] || t.en;

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    const verifyEmail = async () => {
      try {
        await axios.post(`${API}/auth/verify-email`, { token });
        setStatus('success');
        // Refresh user data if logged in
        if (isAuthenticated && refreshUser) {
          refreshUser();
        }
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || text.invalidToken);
      }
    };

    verifyEmail();
  }, [token, isAuthenticated, refreshUser, text.invalidToken]);

  const handleResend = async () => {
    if (!isAuthenticated) return;
    
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {});
      setResent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-pp-bg">
      <Header />
      
      <main className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="pp-container">
          <div className="max-w-md mx-auto text-center">
            {status === 'verifying' && (
              <>
                <div className="w-16 h-16 rounded-full bg-pp-navy/10 mx-auto mb-6 flex items-center justify-center">
                  <CircleNotch size={32} className="text-pp-navy animate-spin" />
                </div>
                <h1 className="pp-heading-2 text-pp-text" data-testid="verifying-email">
                  {text.verifying}
                </h1>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 rounded-full bg-pp-success-muted mx-auto mb-6 flex items-center justify-center">
                  <CheckCircle size={32} className="text-pp-success" weight="fill" />
                </div>
                <h1 className="pp-heading-2 text-pp-text mb-3" data-testid="verify-success">
                  {text.successTitle}
                </h1>
                <p className="text-pp-text-secondary mb-8">{text.successMessage}</p>
                {isAuthenticated ? (
                  <Link to="/dashboard" className="pp-btn-primary">
                    {text.goToDashboard}
                  </Link>
                ) : (
                  <Link to="/login" className="pp-btn-primary">
                    {text.login}
                  </Link>
                )}
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 rounded-full bg-pp-error-bg mx-auto mb-6 flex items-center justify-center">
                  <Warning size={32} className="text-pp-error-text" weight="fill" />
                </div>
                <h1 className="pp-heading-2 text-pp-text mb-3" data-testid="verify-error">
                  {text.errorTitle}
                </h1>
                <p className="text-pp-text-secondary mb-8">{error}</p>
                {isAuthenticated && (
                  <button 
                    onClick={handleResend}
                    disabled={resending || resent}
                    className="pp-btn-primary disabled:opacity-50"
                  >
                    {resending ? text.resending : resent ? text.resent : text.resend}
                  </button>
                )}
                {!isAuthenticated && (
                  <Link to="/login" className="pp-btn-primary">
                    {text.login}
                  </Link>
                )}
              </>
            )}

            {status === 'no-token' && (
              <>
                <div className="w-16 h-16 rounded-2xl bg-pp-navy/10 mx-auto mb-6 flex items-center justify-center">
                  <EnvelopeSimple size={32} className="text-pp-navy" weight="duotone" />
                </div>
                <h1 className="pp-heading-2 text-pp-text mb-3" data-testid="verify-pending">
                  {text.resendTitle}
                </h1>
                <p className="text-pp-text-secondary mb-8">{text.resendMessage}</p>
                {isAuthenticated && !resent && (
                  <button 
                    onClick={handleResend}
                    disabled={resending}
                    className="pp-btn-secondary"
                  >
                    {resending ? text.resending : text.resend}
                  </button>
                )}
                {resent && (
                  <p className="text-pp-success-text font-medium">{text.resent}</p>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
